// src/pages/PhoneNumbers.tsx

import React, { useState, useEffect } from 'react';
import { Phone, Plus, Edit2, Trash2, Globe, Network, Shuffle, Search, X, Building2 } from 'lucide-react';
import { phonesService } from 'api/services/phones.service';
import { PhoneNumber, PaginatedResponse } from 'types';
import { useApi } from 'hooks/useApi';
import { useAuth } from 'hooks/useAuth';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';
import Pagination from 'components/Common/Pagination';
import PhoneModal from 'components/Modals/PhoneModal';
import OrganizationFilter from 'components/Filters/OrganizationFilter';
import { ITEMS_PER_PAGE, LANGUAGES } from 'utils/constants';
import { isOrganizationScopeError } from 'utils/helpers';
import { organizationsService, Organization } from 'api/services/organizations.service';

const PhoneNumbers: React.FC = () => {
  const { currentOrganization, user } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingPhone, setEditingPhone] = useState<PhoneNumber | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string | 'all'>('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Debounce the search box so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setCurrentPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const currentRole = (currentOrganization?.role || user?.role)?.toLowerCase();

  // operator_id/service_id are organization UUIDs; build an id->name map so the list
  // shows friendly names (e.g. "Bajaj_Feedback" / "XtremeGenAI") instead of raw UUIDs.
  const [orgNames, setOrgNames] = useState<Record<string, string>>({});
  useEffect(() => {
    (async () => {
      const map: Record<string, string> = {};
      const [svc, biz] = await Promise.all([
        organizationsService.getServiceProviders(),
        organizationsService.getAllBusinesses(),
      ]);
      [svc, biz].forEach((r) => {
        if (r.success && r.data) r.data.forEach((o) => { map[o.id] = o.name; });
      });
      setOrgNames(map);
    })();
  }, []);

  // Determine organizationId to pass to API
  const getOrganizationId = () => {
    if (currentRole === 'master_admin' || currentRole === 'super_admin') {
      return selectedOrgId === 'all' ? undefined : selectedOrgId;
    }
    return currentOrganization?.id;
  };

  const { data, loading, error, refetch } = useApi<PaginatedResponse<PhoneNumber>>(
    () => phonesService.getPhoneNumbers({
      page: currentPage,
      perPage: ITEMS_PER_PAGE,
      organizationId: getOrganizationId(),
      search: search || undefined
    }),
    [currentPage, selectedOrgId, currentOrganization?.id, search]
  );

  const handleAdd = () => {
    setEditingPhone(null);
    setShowModal(true);
  };

  const handleEdit = (phone: PhoneNumber) => {
    setEditingPhone(phone);
    setShowModal(true);
  };

  const handleDelete = async (phone: PhoneNumber) => {
    if (!window.confirm('Are you sure you want to delete this phone number?')) {
      return;
    }

    setDeleting(phone.id);
    try {
      // Pass operator_id (business id) as organizationId for proper tenant routing
      const response = await phonesService.deletePhoneNumber(phone.id, phone.operator_id);
      if (response.success) {
        refetch();
      } else {
        alert(response.error || 'Failed to delete phone number');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Failed to delete phone number');
    } finally {
      setDeleting(null);
    }
  };

  const handleSave = async (phoneData: Omit<PhoneNumber, 'id'>) => {
    try {
      let response;
      // Use the operator_id (business id) from the phone data as organizationId for proper tenant routing
      const organizationId = phoneData.operator_id;

      if (editingPhone) {
        response = await phonesService.updatePhoneNumber(editingPhone.id, phoneData, organizationId);
      } else {
        response = await phonesService.createPhoneNumber(phoneData, organizationId);
      }

      if (response.success) {
        setShowModal(false);
        refetch();
      } else {
        alert(response.error || 'Failed to save phone number');
      }
    } catch (error) {
      console.error('Save failed:', error);
      alert('Failed to save phone number');
    }
  };

  const getLanguageLabel = (code: string) => {
    const lang = LANGUAGES.find(l => l.value === code);
    return lang ? lang.label : code.toUpperCase();
  };

  // Check if error is "Organization not found" - in this case, show error but keep filters visible
  // One matcher, in helpers — see isOrganizationScopeError for why this must
  // not be a literal comparison against the server's wording.
  const isOrgNotFoundError = isOrganizationScopeError(error);

  if (loading && !data) return <LoadingSpinner />;
  // For "Organization not found" error, continue rendering to show organization filter
  if (error && !isOrgNotFoundError && !data) return <ErrorMessage message={error} onRetry={refetch} />;
  if (!data && !isOrgNotFoundError) return null;

  // Check if user has admin privileges in current organization (only if data is available)
  const hasAdminAccess = data && (currentRole === 'master_admin' ||
                         currentRole === 'super_admin' ||
                         currentRole === 'service_admin' ||
                         currentRole === 'operator_admin');

  // Check if user can add phone numbers (master_admin, super_admin, and service_admin)
  const canAddPhoneNumber = data && (currentRole === 'master_admin' ||
                            currentRole === 'super_admin' ||
                            currentRole === 'service_admin');

  // Check if user is business admin (operator_admin)
  const isBusinessAdmin = data && currentRole === 'operator_admin';

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
            Phone Numbers
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {data && `Total: ${data.total} numbers configured`}
            {data && !hasAdminAccess && <span className="ml-2 text-xs text-orange-600">(Read-only)</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {canAddPhoneNumber && (
            <button
              onClick={handleAdd}
              className="tl-btn tl-btn-primary"
            >
              <Plus className="w-5 h-5" />
              Add Phone Number
            </button>
          )}
          {isBusinessAdmin && (
            <button
              disabled
              className="px-4 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed flex items-center gap-2"
              title="Feature coming soon"
            >
              <Phone className="w-5 h-5" />
              Buy Phone Number
            </button>
          )}
        </div>
      </div>

      {/* Organization Filter - For master_admin, super_admin */}
      {(currentRole === 'master_admin' || currentRole === 'super_admin') && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <OrganizationFilter
            value={selectedOrgId === 'all' ? '' : selectedOrgId}
            onChange={(value) => {
              setSelectedOrgId(value || 'all');
              setCurrentPage(1);
            }}
            className="w-full md:w-96"
            allowServiceProviderSelection={true}
          />
        </div>
      )}

      {/* Search numbers — number, display name, or business */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="relative w-full md:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by number, display name, or business…"
            className="w-full pl-9 pr-9 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              title="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {search && data && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {data.total} result{data.total === 1 ? '' : 's'} for “{search}”
          </p>
        )}
      </div>

      {/* Show error message if organization not found, but keep filters visible */}
      {isOrgNotFoundError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">Organization Not Found</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <p className="mt-1 text-sm text-red-600">Please select a different organization from the dropdown above.</p>
            </div>
          </div>
        </div>
      )}

      {!isOrgNotFoundError && data && <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Phone Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Display Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Routing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Language
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Service Info
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {data.data.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                    {search
                      ? <>No numbers match “{search}”.</>
                      : <>No phone numbers{getOrganizationId() ? ' for this organization (incl. sub-organizations)' : ''} yet.</>}
                  </td>
                </tr>
              )}
              {data.data.map((phone) => (
                <tr key={phone.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Phone className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-gray-900 dark:text-white">
                        {phone.cli_number}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-gray-900 dark:text-white">{phone.transcribe_name}</p>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5">
                      <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={phone.organization_name || phone.business_id || ''}>
                          {phone.organization_name || phone.business_id || '—'}
                        </p>
                        {phone.business_id && phone.organization_name && phone.organization_name !== phone.business_id && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{phone.business_id}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {phone.routing_type === 'sip_trunk' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                        <Network className="w-3 h-3" />
                        SIP Trunk
                      </span>
                    ) : phone.routing_type === 'hybrid' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-medium">
                        <Shuffle className="w-3 h-3" />
                        Hybrid
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                        <Phone className="w-3 h-3" />
                        IVR/Agent
                      </span>
                    )}
                    {phone.sip_trunk_uri && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate max-w-xs" title={phone.sip_trunk_uri}>
                        {phone.sip_trunk_uri}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      <Globe className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-900 dark:text-white">
                        {getLanguageLabel(phone.default_language)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <p className="text-gray-600 dark:text-gray-400">
                        Operator: <span className="font-medium text-gray-900 dark:text-white">{orgNames[phone.operator_id] || phone.organization_name || phone.operator_id}</span>
                      </p>
                      <p className="text-gray-600 dark:text-gray-400">
                        Service: <span className="font-medium text-gray-900 dark:text-white">{orgNames[phone.service_id] || phone.service_id}</span>
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {hasAdminAccess ? (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEdit(phone)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {!isBusinessAdmin && (
                          <button
                            onClick={() => handleDelete(phone)}
                            disabled={deleting === phone.id}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                            title="Delete"
                          >
                            {deleting === phone.id ? (
                              <div className="w-4 h-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <span className="text-xs text-gray-400 italic">View Only</span>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data.data.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
            <Pagination
              currentPage={currentPage}
              totalPages={data.totalPages}
              onPageChange={setCurrentPage}
              totalItems={data.total}
              itemsPerPage={ITEMS_PER_PAGE}
            />
          </div>
        )}
      </div>}

      {showModal && (
        <PhoneModal
          phone={editingPhone}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default PhoneNumbers;